import React, { useState } from "react"
import { TextStyle, TouchableOpacity, View, ViewStyle } from "react-native"

import { UserRole } from "types/role"

import type { ThemedStyle } from "src/theme"
import { Alert } from "src/components/Alert"
import { Text } from "src/components/Ignite"
import { useRole } from "src/context/RoleContext"
import { useAppTheme } from "src/hooks/useAppTheme"
import { useAvailableRoles, useCurrentRole, useRoleInfo } from "src/queries/useRole"
import { getErrorMessage } from "src/utils/errorUtils"

interface RoleSwitcherProps {
  onClose?: () => void
  /** Called before role switch starts - useful for hiding avatar */
  onSwitchStart?: () => void
  /** Called after role switch fails - useful for restoring avatar */
  onSwitchError?: () => void
  style?: ViewStyle
}

const ROLE_DISPLAY_NAMES: Record<UserRole, string> = {
  sponsor_admin: "Program Manager",
  sme: "Expert",
  member: "End User",
  testing_member: "End User - Test",
}

const ROLE_DESCRIPTIONS: Record<UserRole, string> = {
  sponsor_admin: "Your default role",
  sme: "Get help creating content modules",
  member: "Test the app as an end user",
  testing_member: "Try testing and pending modules as an end user",
}

export const RoleSwitcher: React.FC<RoleSwitcherProps> = ({
  onClose,
  onSwitchStart,
  onSwitchError,
  style,
}) => {
  const { themed } = useAppTheme()
  const { switchRole, isSwitchingRole } = useRole()
  const currentRole = useCurrentRole()
  const availableRoles = useAvailableRoles()
  const roleInfo = useRoleInfo()
  const [isProcessing, setIsProcessing] = useState(false)
  const [pendingRole, setPendingRole] = useState<UserRole | null>(null)

  const handleRoleSwitch = async (targetRole: UserRole) => {
    if (targetRole === currentRole) {
      return
    }

    const targetRoleName = ROLE_DISPLAY_NAMES[targetRole]

    setPendingRole(targetRole)

    Alert.alert(
      "Confirm Role Change",
      `Are you sure you want to change to the ${targetRoleName} role?\n\nYou can change your role at any time.`,
      [
        {
          text: "CANCEL",
          style: "cancel",
          onPress: () => {
            setPendingRole(null)
          },
        },
        {
          text: "CONFIRM",
          style: "default",
          onPress: async () => {
            try {
              setIsProcessing(true)
              onSwitchStart?.()
              await switchRole(targetRole)
              onClose?.()
            } catch (error) {
              const errorMessage = getErrorMessage(error)
              Alert.alert(
                "Role Switch Failed",
                `Failed to switch to ${targetRoleName}.\n\n${errorMessage}`,
                [{ text: "OK" }],
              )
              onSwitchError?.()
            } finally {
              setPendingRole(null)
              setIsProcessing(false)
            }
          },
        },
      ],
    )
  }

  if (!currentRole || availableRoles.length === 0) {
    return (
      <View style={themed($container)}>
        <Text style={themed($title)}>Role Management</Text>
        <Text style={themed($subtitle)}>No role information available</Text>
      </View>
    )
  }

  return (
    <View style={[themed($container), style]}>
      <Text style={themed($subtitle)}>
        Preview the experience of other roles on the Sidekick Platform.
      </Text>

      <View style={themed($roleList)}>
        {availableRoles.map((role) => {
          const isDefault = role.user_id === roleInfo?.main_user_id
          const highlightedRole = pendingRole ?? currentRole
          const isCurrentRole = role.role === highlightedRole

          return (
            <TouchableOpacity
              key={role.role}
              style={[themed($roleCard), isCurrentRole && themed($currentRoleCard)]}
              onPress={() => handleRoleSwitch(role.role)}
              disabled={role.role === currentRole || isSwitchingRole || isProcessing}
              activeOpacity={0.7}
            >
              <Text
                style={[themed($roleCardTitle), isCurrentRole && themed($currentRoleCardTitle)]}
              >
                {ROLE_DISPLAY_NAMES[role.role]}
              </Text>
              <Text
                style={[
                  themed($roleCardDescription),
                  isCurrentRole && themed($currentRoleCardDescription),
                ]}
              >
                {isDefault ? "Your default role" : ROLE_DESCRIPTIONS[role.role]}
              </Text>
            </TouchableOpacity>
          )
        })}
      </View>
    </View>
  )
}

const $container: ThemedStyle<ViewStyle> = ({ colors }) => ({
  padding: 20,
  paddingTop: 75,
  minHeight: 300,
  flex: 1,
  justifyContent: "flex-start",
  backgroundColor: colors.background,
})

const $title: ThemedStyle<TextStyle> = ({ colors }) => ({
  fontSize: 20,
  fontWeight: "bold",
  marginBottom: 8,
  textAlign: "left",
  color: colors.text,
})

const $subtitle: ThemedStyle<TextStyle> = ({ colors }) => ({
  fontSize: 16,
  lineHeight: 24,
  marginBottom: 20,
  textAlign: "left",
  opacity: 0.7,
  color: colors.text,
})

const $roleList: ThemedStyle<ViewStyle> = () => ({
  marginBottom: 20,
})

const $roleCard: ThemedStyle<ViewStyle> = ({ colors }) => ({
  backgroundColor: colors.buttonBackground,
  borderRadius: 12,
  padding: 16,
  marginBottom: 12,
})

const $currentRoleCard: ThemedStyle<ViewStyle> = ({ colors }) => ({
  backgroundColor: colors.buttonBackground,
  borderWidth: 2,
  borderColor: colors.menuButtonText,
})

const $roleCardTitle: ThemedStyle<TextStyle> = ({ colors }) => ({
  fontSize: 18,
  fontWeight: "600",
  color: colors.menuButtonText,
  marginBottom: 4,
})

const $currentRoleCardTitle: ThemedStyle<TextStyle> = ({ colors }) => ({
  color: colors.menuButtonText,
})

const $roleCardDescription: ThemedStyle<TextStyle> = ({ colors }) => ({
  fontSize: 14,
  lineHeight: 20,
  color: colors.textDim,
})

const $currentRoleCardDescription: ThemedStyle<TextStyle> = ({ colors }) => ({
  color: colors.textDim,
})
